import React from 'react';
import { useWatch } from 'react-hook-form';
import { Headline } from '@sato/components';
import { observer } from 'mobx-react-lite';
import * as yup from 'yup';

import { IWidget, ParsedEntry } from '@/contentful/types';

import { HookFormTextInput } from '@components/ReactHookFormFields/HookFormTextInput';
import { useCustomTranslations } from '@hooks/useCustomTranslations';
import { DisturbanceLocation } from '@models/api/Incident';
import { apartmentStore } from '@stores/apartmentStore';
import { formatDateToPlainDateInTimeZone } from '@utils/date.utils';

import { IncidentFormWrapper } from '../IncidentFormWrapper';

import {
  ApartmentsInRealEstateDropdown,
  OTHER_APARTMENT_OPTION,
} from './ApartmentsInRealEstateDropdown';
import {
  DisturbanceReportFormBase,
  useDisturbanceReport,
} from './Disturbance.utils';
import { DisturbanceCommonForm } from './DisturbanceCommonForm';

interface NeighbourForm extends DisturbanceReportFormBase {
  disturbanceApartmentId?: string;
  unlistedDisturbanceApartment?: string;
}

export const DisturbanceNeighbourForm = (props: ParsedEntry<IWidget>) => {
  const { create, defaultInitialValues, defaultValidationSchema } =
    useDisturbanceReport();

  return (
    <IncidentFormWrapper<NeighbourForm>
      incidentKind="DisturbanceNeighbour"
      defaultValues={{
        ...defaultInitialValues,
        disturbanceApartmentId: undefined,
        unlistedDisturbanceApartment: '',
      }}
      validationSchema={defaultValidationSchema.concat(
        yup.object().shape({
          disturbanceApartmentId: yup.string(),
          unlistedDisturbanceApartment: yup
            .string()
            .trim()
            .when('disturbanceApartmentId', {
              is: OTHER_APARTMENT_OPTION,
              then: (schema) => schema.required(),
            }),
        })
      )}
      onSubmit={({
        disturbanceTimes,
        disturbanceApartmentId,
        unlistedDisturbanceApartment,
        ...values
      }) => {
        const isOtherApartment =
          disturbanceApartmentId === OTHER_APARTMENT_OPTION;

        return create({
          ...values,
          disturbanceApartment: isOtherApartment
            ? unlistedDisturbanceApartment
            : apartmentStore.selectedApartment?.realestate.apartmentDetails.find(
                (apartmentDetail) =>
                  apartmentDetail.apartmentId === disturbanceApartmentId
              )?.number,
          disturbanceApartmentId: isOtherApartment
            ? undefined
            : disturbanceApartmentId,
          disturbanceLocation: DisturbanceLocation.APARTMENT,
          dateTime: disturbanceTimes.map((date) =>
            formatDateToPlainDateInTimeZone(date)
          ),
        });
      }}
    >
      <DisturbanceApartmentFields {...props} />

      <DisturbanceCommonForm {...props} />
    </IncidentFormWrapper>
  );
};

/**
 * Apartment dropdown, with an "other" option and a free text field when the real estate isn't fully
 * owned by SATO (so the list may not contain all apartments)
 */
const DisturbanceApartmentFields = observer((props: ParsedEntry<IWidget>) => {
  const { t } = useCustomTranslations(props.customTranslations);
  const disturbanceApartmentId = useWatch<
    NeighbourForm,
    'disturbanceApartmentId'
  >({ name: 'disturbanceApartmentId' });

  const isNotFullySatoProperty =
    apartmentStore.selectedApartment?.realestate.managementModel ===
    'NOT_FULL_SATO_TERRITORY';

  return (
    <>
      <Headline level={3} className="mb-3">
        {t('disturbanceApartmentHeading')}
      </Headline>

      <ApartmentsInRealEstateDropdown
        name="disturbanceApartmentId"
        label={t('disturbanceApartmentLabel')}
        otherOptionLabel={
          isNotFullySatoProperty
            ? t('disturbanceApartmentOtherOption')
            : undefined
        }
      />

      {disturbanceApartmentId === OTHER_APARTMENT_OPTION && (
        <HookFormTextInput
          name="unlistedDisturbanceApartment"
          label={{
            value: t('unlistedDisturbanceApartmentLabel'),
            type: 'static',
          }}
          maxLength={50}
        />
      )}
    </>
  );
});
